import { useEffect, useContext, useState } from 'react'
import { Button, Skeleton, Box } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import _ from 'lodash'

import AppleFront from '/components/editor/apple/front'
import AppleLockScreen from '/components/editor/apple/lock-screen'
import AppleBack from '/components/editor/apple/back'

import GoogleLegacyFront from '/components/editor/google-legacy/front'
import GoogleLegacyListItem from '/components/editor/google-legacy/list-item'
import GoogleLegacyBack from '/components/editor/google-legacy/back'
import GoogleLegacyAppLink from '/components/editor/google-legacy/fronts/components/app-link'

import GoogleFront from '/components/editor/google/front'
import GoogleListItem from '/components/editor/google/list-item'
import GoogleBack from '/components/editor/google/back'

import Settings from './settings'
import detectMinFontSizeOverride from '/utils/detect-min-font-size-override.js'

import { AppContext } from "/providers/app-context"
import { PassContext } from "./pass-context"
import { nextEditableHighlightMode } from '/utils/editable-highlight-mode'

import {getGoogleBackgroundColour,getGoogleDarkBackgroundColour,contrastColor} from './google/colour-tool.js'

const AppBody = ({ isManagedPassTemplate, templateLoaded, templateId, smartixPassType, variableCatalog, appleModel, appleTemplate, googleObject, variables, setVariables, scannerData, setScannerData, googleClass, applePassType, appleSubType, googlePassType, googleSubType, setAppleModel, setAppleTemplate, setGoogleClass, setGoogleObject, metaData, setMetaData, setGoogleBoth, openVariablesManager, applePreviewVersion, canInitiateGooglePreview, googlePreviewSaving, initiateGooglePreview, googleClassPushing, pushGoogleClass }) => {

  const { client, selectedClientId, user, saveUser } = useContext(AppContext)
  const [googleRenderer, setGoogleRenderer] = useState('google')

  const clientId = selectedClientId ?? client?.client_id

  useEffect(() => {
    if (user) setGoogleRenderer(user.preferences?.google_renderer ?? 'google')
  }, [user])

  const effectiveGoogleRenderer = googlePassType === 'transit' ? 'legacy' : googleRenderer

  useEffect(() => {
    const info = detectMinFontSizeOverride()
    if (info.overridden) {
      enqueueSnackbar({
        message: <>
          Preview note: your browser’s minimum text size setting is active.<br />
          See Settings &gt; Advanced &gt; Never use font sizes less than...<br />
          <br />
          Smaller fonts (eg. labels) may be rendered larger than they will appear on devices.<br />
          <br />
          We suggest you either disable this feature whilst using the editor or switch to Chrome browser.
        </>, variant: 'warning', persist: true
      })
    }
  }, [])

  const toggleEditAreaVisibility = () => {
    let u = _.cloneDeep(user)
    u.preferences.highlight_editable_items = nextEditableHighlightMode(u.preferences.highlight_editable_items)
    saveUser(u)
  }

  useEffect(() => {
    if (!user) return

    const handleKeydown = event => {
      // Ctrl+Alt/Option+E to toggle edit area highlighting
      const isE = event.code === 'KeyE' || String(event.key).toLowerCase() === 'e'
      if (isE && event.ctrlKey && event.altKey) {
        event.preventDefault()
        event.stopPropagation()
        toggleEditAreaVisibility()
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [user])


  const googleBackgroundColour = getGoogleBackgroundColour({ googleClass, googleObject, variables, googlePassType })
  const googleDarkBackgroundColour = getGoogleDarkBackgroundColour({googleBackgroundColour})
  const googleFgColour = contrastColor({ bgColor: googleBackgroundColour })

    const googleStyles = {
      card: {
        background: `linear-gradient(
          to bottom,
          ${googleBackgroundColour} 0px,
          ${googleBackgroundColour} 350px,
          ${googleDarkBackgroundColour} 750px,
          ${googleDarkBackgroundColour} 100%
        )`
      }
    }


  return (
    <div className='flex flex-row justify-between gap-10 w-full h-full overflow-y-auto pl-5 pr-2'>
      <div className='flex flex-wrap gap-5h-full pt-5 pb-10'>
        <div className='w-[400px] flex flex-col gap-10'>
          <div className="apple">
            <div>Apple IOS Lock Screen ({applePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "apple", clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, model: appleModel, template: appleTemplate, passType: applePassType, subType: appleSubType, setModel: setAppleModel, setTemplate: setAppleTemplate, variables, setVariables, scannerData, setScannerData }}>
                    <AppleLockScreen />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={100} />}
              </div>
            </div>
          </div>

          <div className="apple">

            <div>Apple IOS Front ({applePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "apple", allowImageCors: true, imageCorsKey: applePreviewVersion, clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, isManagedPassTemplate, model: appleModel, template: appleTemplate, passType: applePassType, subType: appleSubType, setModel: setAppleModel, setTemplate: setAppleTemplate, metaData, setMetaData, variables, setVariables, scannerData, setScannerData }}>
                    <AppleFront />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>
            </div>
          </div>

          <div className="apple">
            <div>Apple IOS Back</div>

            <div className="wrapper mt-5" style={{ borderRadius: 10 }}> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass -mt-5">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "apple", clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, isManagedPassTemplate, model: appleModel, template: appleTemplate, passType: applePassType, subType: appleSubType, setModel: setAppleModel, setTemplate: setAppleTemplate, variables, setVariables, scannerData, setScannerData, applePreviewVersion }}>
                    <AppleBack />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>
            </div>
          </div>
        </div>

        <div className='w-[470px] flex flex-col gap-10 mr-10'>
          {effectiveGoogleRenderer === 'legacy' && <>
          <div className="google-legacy">
            <div>Google Android Legacy List item ({googlePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "google", clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, variables, setVariables, scannerData, setScannerData }}>
                    <GoogleLegacyListItem />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={100} />}

              </div>
            </div>
          </div>

          <div className="google-legacy">
            <div>Google Android Legacy Front ({googlePassType})</div>

            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "google", allowImageCors: true, imageCorsKey: applePreviewVersion, clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, isManagedPassTemplate, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, metaData, setMetaData, setGoogleBoth, variables, setVariables, scannerData, setScannerData }}>
                    <GoogleLegacyFront />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>

              {templateLoaded &&
                <PassContext.Provider value={{ openVariablesManager, classData: googleClass, objectData: googleObject, passType: googlePassType, setClassData: setGoogleClass, setObjectData: setGoogleObject, setGoogleBoth }}>
                  <GoogleLegacyAppLink />
                </PassContext.Provider>
              }

            </div>
          </div>

          <div className="google-legacy">
            <div>Google Android Legacy Back</div>
            <div className="wrapper"> {/* Put apple pass on a grey background with drop shadow */}
              <div className="pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "google", clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, isManagedPassTemplate, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, setGoogleBoth, variables, setVariables, scannerData, setScannerData }}>
                    <GoogleLegacyBack />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={600} />}

              </div>
            </div>
          </div>

          </>}

          {effectiveGoogleRenderer === 'google' && <>
          <div className="google">
            <div>Google Android List item ({googlePassType})</div>

            <div className="wrapper">
              <div className="pass google-list-item-pass">

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "google", clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, variables, setVariables, scannerData, setScannerData }}>
                    <GoogleListItem />
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={100} />}

              </div>
            </div>
          </div>

          <div className="google">
            <div>Google Android ({googlePassType})</div>

            <div className="wrapper"> 
              <div className="pass" style={googleStyles.card} >

                {templateLoaded &&
                  <PassContext.Provider value={{ openVariablesManager, device: "google", allowImageCors: true, imageCorsKey: applePreviewVersion, clientId, templateId, smartixPassType, variableCatalog, variablesCatalog: variableCatalog, isManagedPassTemplate, classData: googleClass, objectData: googleObject, passType: googlePassType, subType: googleSubType, setClassData: setGoogleClass, setObjectData: setGoogleObject, metaData, setMetaData, setGoogleBoth, variables, setVariables, scannerData, setScannerData }}>
                    <div id="googleFrontPreview">
                      <GoogleFront backgroundColour={googleBackgroundColour} googleDarkBackgroundColour={googleDarkBackgroundColour} fgColour={googleFgColour}/>
                    </div>
                    <GoogleBack backgroundColour={googleDarkBackgroundColour} fgColour={googleFgColour}/>
                  </PassContext.Provider>
                }

                {!templateLoaded && <Skeleton variant="rectangular" width="100%" height={1000} />}

              </div>
            </div>
          </div>

          </>}
        </div>

        {canInitiateGooglePreview &&
          <div className="flex flex-col items-start gap-3 pt-5">
            <Button
              variant="outlined"
              onClick={initiateGooglePreview}
              disabled={googlePreviewSaving || googleClassPushing || !templateLoaded}
            >
              {googlePreviewSaving ? 'Uploading...' : 'Snapshot previews'}
            </Button>
            <Button
              variant="outlined"
              onClick={pushGoogleClass}
              disabled={googlePreviewSaving || googleClassPushing || !templateLoaded}
            >
              {googleClassPushing ? 'Pushing...' : 'Push Google class'}
            </Button>
          </div>
        }

      </div>

      <Box sx={{ position: "sticky", top: 0, bottom: 0, width: "390px", zIndex: 1100 }} >
        <PassContext.Provider value={{ openVariablesManager, templateId, smartixPassType, applePassType, appleSubType, googlePassType, variables, setVariables, model: appleModel, template: appleTemplate, setModel: setAppleModel, setTemplate: setAppleTemplate, classData: googleClass, objectData: googleObject, setClassData: setGoogleClass, setObjectData: setGoogleObject, metaData, setMetaData }}>
          <Settings />
        </PassContext.Provider>
      </Box>

    </div>
  )
}

export default AppBody
